/**
 * Google Forms Integration for Wonderpath EcoGames - Early Childhood Edition
 *
 * Setup Instructions:
 * 1. You already have your Google Form created at:
 *    https://docs.google.com/forms/d/e/1FAIpQLSfVAgud-TcPVd9t1Ut7SgfJcO08t9WXT-FvtskpFcJwEcQT1w/viewform
 *
 * 2. Get pre-filled link to find entry IDs:
 *    - Open form in Edit mode
 *    - Click ⋮ (three dots) → "Get pre-filled link"
 *    - Fill in ALL fields with test data
 *    - Click "Get link"
 *    - The URL will show entry IDs like: entry.123456789=test&entry.987654321=test
 *
 * 3. Replace the placeholder entry IDs below with your actual IDs
 */

// ============================================
// CONFIGURATION - UPDATE THESE VALUES
// ============================================

export const GOOGLE_FORM_CONFIG = {
  // Your form submission URL (change /viewform to /formResponse)
  formUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSfVAgud-TcPVd9t1Ut7SgfJcO08t9WXT-FvtskpFcJwEcQT1w/formResponse',

  // Entry IDs from your Google Form
  // Get these by creating a pre-filled link (see instructions above)
  entryIds: {
    relationship: 'entry.XXXXXXXXX',        // Q1: Parent/Grandparent/Teacher/etc
    childAge: 'entry.XXXXXXXXX',            // Q2: 3/4/5/6/7+
    enjoymentRating: 'entry.XXXXXXXXX',     // Q3: 1-5 scale (liked Benny game)
    difficultyRating: 'entry.XXXXXXXXX',    // Q4: 1-3 scale (difficulty)
    natureThemes: 'entry.XXXXXXXXX',        // Q5: Checkboxes (water, animals, insects, etc)
    childLoves: 'entry.XXXXXXXXX',          // Q6: What child loves about nature
    improvements: 'entry.XXXXXXXXX',        // Q7: How to make better
    childAsked: 'entry.XXXXXXXXX',          // Q8: What child asked for
    email: 'entry.XXXXXXXXX',               // Q9: Email opt-in
    language: 'entry.XXXXXXXXX',            // Hidden: Language (en/es)
    timestamp: 'entry.XXXXXXXXX',           // Hidden: Submission timestamp
  }
};

// ============================================
// NATURE THEME LABELS (Match your form exactly)
// ============================================

// Map theme IDs to their full labels as they appear in the Google Form
export const NATURE_THEME_LABELS = {
  en: {
    'water': 'Water themes',
    'animals': 'Wild animal personalities',
    'insects': 'The secret life of insects',
    'fungi': 'The magic of fungi and microbes',
    'earth-care': 'Caring for the Earth',
    'other': 'Other',
  },
  es: {
    'water': 'Temas de agua',
    'animals': 'Personalidades de animales salvajes',
    'insects': 'La vida secreta de los insectos',
    'fungi': 'La magia de los hongos y microbios',
    'earth-care': 'Cuidar la Tierra',
    'other': 'Otro',
  }
};

// ============================================
// SUBMISSION FUNCTION
// ============================================

/**
 * Submit feedback data to Google Form
 *
 * @param data - Feedback data from early childhood feedback form
 * @returns Promise with success status
 */
export async function submitToGoogleForm(data: {
  relationship: string;
  childAge: string;
  enjoymentRating: string;
  difficultyRating: string;
  selectedThemes: string[];
  childLoves: string;
  improvements: string;
  childAsked: string;
  email: string;
  language: 'en' | 'es';
}): Promise<{ success: boolean; error?: any }> {

  // Check if form is configured
  if (GOOGLE_FORM_CONFIG.entryIds.relationship.includes('XXXXXXXXX')) {
    console.warn('⚠️ Google Form not configured yet. Update googleForms.ts with your form entry IDs.');
    console.log('📋 Feedback data (would be submitted):', data);
    return { success: false, error: 'Form not configured' };
  }

  try {
    const formData = new FormData();

    // Add basic responses
    if (data.relationship) {
      formData.append(GOOGLE_FORM_CONFIG.entryIds.relationship, data.relationship);
    }

    if (data.childAge) {
      formData.append(GOOGLE_FORM_CONFIG.entryIds.childAge, data.childAge);
    }

    if (data.enjoymentRating) {
      formData.append(GOOGLE_FORM_CONFIG.entryIds.enjoymentRating, data.enjoymentRating);
    }

    if (data.difficultyRating) {
      formData.append(GOOGLE_FORM_CONFIG.entryIds.difficultyRating, data.difficultyRating);
    }

    // Add selected nature themes (checkboxes)
    const themeLabels = NATURE_THEME_LABELS[data.language];
    data.selectedThemes.forEach(themeId => {
      const label = themeLabels[themeId as keyof typeof themeLabels];
      if (label) {
        formData.append(GOOGLE_FORM_CONFIG.entryIds.natureThemes, label);
      }
    });

    // Add text responses (only if they have content)
    if (data.childLoves && data.childLoves.trim()) {
      formData.append(GOOGLE_FORM_CONFIG.entryIds.childLoves, data.childLoves.trim());
    }

    if (data.improvements && data.improvements.trim()) {
      formData.append(GOOGLE_FORM_CONFIG.entryIds.improvements, data.improvements.trim());
    }

    if (data.childAsked && data.childAsked.trim()) {
      formData.append(GOOGLE_FORM_CONFIG.entryIds.childAsked, data.childAsked.trim());
    }

    if (data.email && data.email.trim()) {
      formData.append(GOOGLE_FORM_CONFIG.entryIds.email, data.email.trim());
    }

    // Add metadata
    formData.append(GOOGLE_FORM_CONFIG.entryIds.language, data.language);
    formData.append(GOOGLE_FORM_CONFIG.entryIds.timestamp, new Date().toISOString());

    // Submit to Google Form
    // Note: mode: 'no-cors' is required for Google Forms
    // This means we won't get a response back, but the submission will work
    await fetch(GOOGLE_FORM_CONFIG.formUrl, {
      method: 'POST',
      body: formData,
      mode: 'no-cors', // Required for Google Forms cross-origin submission
    });

    console.log('✅ Early Childhood Feedback submitted to Google Form successfully');
    console.log('📊 Submitted data:', {
      relationship: data.relationship,
      childAge: data.childAge,
      enjoymentRating: data.enjoymentRating,
      difficultyRating: data.difficultyRating,
      selectedThemes: data.selectedThemes,
      childLoves: data.childLoves ? `${data.childLoves.substring(0, 30)}...` : '(empty)',
      improvements: data.improvements ? `${data.improvements.substring(0, 30)}...` : '(empty)',
      childAsked: data.childAsked ? `${data.childAsked.substring(0, 30)}...` : '(empty)',
      email: data.email ? '(provided)' : '(empty)',
      language: data.language,
    });

    return { success: true };

  } catch (error) {
    console.error('❌ Error submitting feedback to Google Form:', error);
    console.log('📋 Feedback data (failed to submit):', data);
    return { success: false, error };
  }
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Check if Google Form is properly configured
 */
export function isGoogleFormConfigured(): boolean {
  return !GOOGLE_FORM_CONFIG.entryIds.relationship.includes('XXXXXXXXX');
}

/**
 * Get a summary of the form configuration status
 */
export function getConfigStatus(): {
  configured: boolean;
  message: string;
  checklist: { item: string; status: boolean }[];
} {
  const checklist = [
    {
      item: 'Form URL updated',
      status: !GOOGLE_FORM_CONFIG.formUrl.includes('YOUR_FORM_ID')
    },
    {
      item: 'Relationship entry ID set',
      status: !GOOGLE_FORM_CONFIG.entryIds.relationship.includes('XXXXXXXXX')
    },
    {
      item: 'Child age entry ID set',
      status: !GOOGLE_FORM_CONFIG.entryIds.childAge.includes('XXXXXXXXX')
    },
    {
      item: 'Ratings entry IDs set',
      status: !GOOGLE_FORM_CONFIG.entryIds.enjoymentRating.includes('XXXXXXXXX')
    },
    {
      item: 'Nature themes entry ID set',
      status: !GOOGLE_FORM_CONFIG.entryIds.natureThemes.includes('XXXXXXXXX')
    },
  ];

  const configured = checklist.every(item => item.status);

  return {
    configured,
    message: configured
      ? '✅ Google Form is fully configured and ready!'
      : '⚠️ Google Form needs configuration. Fill in entry IDs in googleForms.ts',
    checklist
  };
}
